import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FeedService } from '../../services/feedService';
import { WatchlistService } from '../../services/watchlistService';
import styles from './ActivityFeed.module.css';
import { SignatureBadge } from '../CoinMedia/CoinMedia';
import { resolveCoinMedia } from '../../utils/coinMedia';
import { FoundingCollectorIcon } from '../FoundingCollectorBadge/FoundingCollectorBadge';

// ─── Constants ────────────────────────────────────────────────────────────────

const REACTIONS = [
    { id: 'impressive',  emoji: '🔥', label: 'Impressive' },
    { id: 'legendary',   emoji: '👑', label: 'Legendary' },
    { id: 'trying',      emoji: '😤', label: "I'm trying this" },
    { id: 'watchlisted', emoji: '👀', label: 'Watchlisted' },
];

const RARITY_CONFIG = {
    legendary: { label: 'LEGENDARY', color: '#D4AF37', glow: 'rgba(212,175,55,0.35)' },
    gold:      { label: 'GOLD',      color: '#FFD700', glow: 'rgba(255,215,0,0.25)'  },
    silver:    { label: 'SILVER',    color: '#C0C0C0', glow: 'rgba(192,192,192,0.2)' },
    bronze:    { label: 'BRONZE',    color: '#CD7F32', glow: 'rgba(205,127,50,0.2)'  },
};

const PREV_RARITY = { gold: 'silver', silver: 'bronze', legendary: 'gold' };

// ─── Tier / CTA resolution ────────────────────────────────────────────────────

const TIER_RANK       = { bronze: 1, silver: 2, gold: 3, legendary: 4 };
const NEXT_TIER_LABEL = { bronze: 'Silver', silver: 'Gold' };
const TIER_DISPLAY    = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', legendary: 'Legendary' };

function buildMyTierMap(myCoins = []) {
    const map = {};
    for (const c of myCoins) {
        const key = String(c.tmdbId ?? c.mediaId ?? '');
        if (!key) continue;
        const rank = TIER_RANK[c.rarity] ?? 0;
        if (!map[key] || rank > (TIER_RANK[map[key]] ?? 0)) {
            map[key] = c.rarity;
        }
    }
    return map;
}

function resolveCTA(baseCTA, myTier) {
    if (baseCTA.action !== 'quiz' && baseCTA.action !== 'coin') {
        return { label: baseCTA.label, subLabel: null, disabled: false, variant: 'default' };
    }
    if (!myTier) {
        return { label: baseCTA.label, subLabel: null, disabled: false, variant: 'default' };
    }
    if (myTier === 'legendary') {
        return { label: '✦ You have Legendary', subLabel: "You've mastered this", disabled: true, variant: 'legendary' };
    }
    if (myTier === 'gold') {
        return { label: '◆ You have Gold', subLabel: "You're at the top personal tier", disabled: true, variant: 'owned' };
    }
    return {
        label:    `Upgrade to ${NEXT_TIER_LABEL[myTier]}`,
        subLabel: `You currently have ${TIER_DISPLAY[myTier]}`,
        disabled: false,
        variant:  'upgrade',
    };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const s = Math.floor((Date.now() - new Date(dateStr)) / 1000);
    if (isNaN(s) || s < 0) return '';
    if (s < 60)    return `${s}s ago`;
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function normaliseCoin(coin) {
    if (!coin) return null;
    return {
        ...coin,
        media: {
            title:        coin.mediaTitle     ?? coin.media?.title        ?? '',
            posterUrl:    coin.mediaPosterUrl ?? coin.media?.posterUrl    ?? null,
            kind:         coin.mediaKind      ?? coin.media?.kind         ?? 'movie',
            tmdbId:       coin.tmdbId         ?? coin.media?.tmdbId       ?? null,
            seasonNumber: coin.seasonNumber   ?? coin.media?.seasonNumber ?? null,
        },
        collection: {
            name: coin.collectionName ?? coin.collection?.name ?? null,
            slug: coin.collectionSlug ?? coin.collection?.slug ?? null,
        },
    };
}

/**
 * Derive watchlistable media from a feed event.
 * Returns { tmdbId, kind, title, posterUrl } or null if nothing watchlistable.
 */
function getWatchlistableMedia(event) {
    const coin = normaliseCoin(event.coin);

    const tmdbId = coin?.media?.tmdbId ?? event.media?.tmdbId ?? null;
    const kind   = coin?.media?.kind   ?? event.media?.kind   ?? null;
    const title  = coin?.media?.title  ?? event.media?.title  ?? null;

    if (!tmdbId || !kind || !['movie', 'tv'].includes(kind)) return null;

    return {
        tmdbId,
        kind,
        title:     title ?? 'this title',
        posterUrl: coin?.media?.posterUrl ?? event.media?.posterUrl ?? null,
    };
}

function getActivityMeta(event) {
    const coin   = normaliseCoin(event.coin);
    const rarity = coin?.rarity ?? 'bronze';
    const rc     = RARITY_CONFIG[rarity] ?? RARITY_CONFIG.bronze;
    const prev   = coin?.previousRarity ?? PREV_RARITY[rarity] ?? 'bronze';
    const prc    = RARITY_CONFIG[prev]  ?? RARITY_CONFIG.bronze;

    const collectionName =
        event.collection?.name ?? coin?.collection?.name ?? coin?.collectionName ?? '';
    const mediaTitle = coin?.media?.title ?? '';

    switch (event.type ?? event.eventType) {
        case 'coin_earned':
            return {
                sentence: `earned a ${rc.label} coin for`, title: mediaTitle,
                context: event.context ?? `Verified · ${rc.label} tier`, status: event.status ?? '',
                cta: { label: 'Take the Challenge', action: 'quiz' }, tierBadge: { label: rc.label, color: rc.color },
            };
        case 'coin_upgrade':
            return {
                sentence: 'upgraded', title: mediaTitle,
                context: event.context ?? `${prc.label} → ${rc.label}`,
                status: event.status ?? 'Top verified viewers of this title',
                cta: { label: 'Try to Beat', action: 'quiz' }, tierBadge: { label: rc.label, color: rc.color },
                showUpgradeArrow: true, prevColor: prc.color, prevLabel: prc.label,
            };
        case 'legendary_unlocked':
            return {
                sentence: 'unlocked', sentenceSuffix: '✦ LEGENDARY', title: collectionName || mediaTitle,
                context: event.context ?? 'Perfect score · Mastery completed',
                status: event.status ?? 'Top 2% of verified viewers',
                cta: { label: 'View Legendary', action: 'coin' }, tierBadge: { label: 'LEGENDARY', color: '#D4AF37' },
            };
        case 'collection_progress':
            return {
                sentence: `is ${event.remaining === 1 ? '1 film' : `${event.remaining} films`} away from unlocking`,
                title: collectionName,
                context: event.context ?? `${event.progress ?? '?'}/${event.total ?? '?'} verified`,
                status: event.status ?? 'Reward: Legendary Collection Coin',
                cta: { label: 'View Collection', action: 'collection' }, tierBadge: null,
            };
        case 'collection_completed':
            return {
                sentence: 'completed', title: collectionName,
                context: event.context ?? `${event.total ?? '?'}/${event.total ?? '?'} verified`,
                status: event.status ?? 'Legendary reward coin unlocked',
                cta: { label: 'View Reward', action: 'collection' }, tierBadge: { label: 'LEGENDARY', color: '#D4AF37' },
            };
        case 'watchlist_add':
            return {
                sentence: 'added to watchlist', title: mediaTitle,
                context: event.context ?? 'Trending to Verify this week',
                status: event.status ?? 'Watch it, verify it, earn the coin.',
                cta: { label: 'Add to Watchlist', action: 'watchlist' }, tierBadge: null,
            };
        case 'trending':
            return {
                sentence: `${event.count ?? '?'} people verified`, title: event.media?.title ?? mediaTitle,
                context: event.context ?? `${event.loveRate ?? '?'}% Loved It by verified viewers`,
                status: event.status ?? 'Trending among verified viewers',
                cta: { label: 'Verify This', action: 'quiz' }, tierBadge: null, isTrending: true,
            };
        default:
            return {
                sentence: 'earned', title: mediaTitle, context: '', status: '',
                cta: { label: 'View', action: 'quiz' }, tierBadge: null,
            };
    }
}

// ─── WatchlistToast ───────────────────────────────────────────────────────────

function WatchlistToast({ title, onAdd, onDismiss, variant = 'prompt' }) {
    const [adding, setAdding] = useState(false);

    async function handleAdd() {
        setAdding(true);
        await onAdd?.();
        setAdding(false);
    }

    const content = {
        prompt:  { body: '👀 Watchlisted reaction added.', sub: `Add ${title} to your watchlist?` },
        already: { body: 'Already on your watchlist 👀', sub: null },
        success: { body: `${title} added to your watchlist.`, sub: null },
        error:   { body: "Couldn't add to watchlist. Please try again.", sub: null },
    }[variant] ?? { body: '👀 Watchlisted reaction added.', sub: null };

    return (
        <div className={`${styles.toast} ${styles[`toast_${variant}`]}`} role="status" aria-live="polite">
            <div className={styles.toastContent}>
                <span className={styles.toastBody}>{content.body}</span>
                {content.sub && <span className={styles.toastSub}>{content.sub}</span>}
            </div>
            <div className={styles.toastActions}>
                {variant === 'prompt' && (
                    <button
                        className={styles.toastAddBtn}
                        onClick={handleAdd}
                        disabled={adding}
                    >
                        {adding ? 'Adding…' : 'Add to Watchlist'}
                    </button>
                )}
                <button className={styles.toastDismissBtn} onClick={onDismiss} aria-label="Dismiss">
                    ✕
                </button>
            </div>
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function PosterThumb({ url, title }) {
    if (!url) return null;
    return (
        <div className={styles.posterThumb}>
            <img src={url} alt={title} className={styles.posterImg} />
        </div>
    );
}

function CoinVisual({ event, coin, rc, isLegendary, showUpgradeArrow, prevColor, prevLabel }) {
    const type         = event.type ?? event.eventType;
    const isCollection = type === 'collection_progress';
    const posterUrl    = coin?.media?.posterUrl ?? null;
    const mediaTitle   = coin?.media?.title ?? '';

    return (
        <div className={styles.coinVisualRow}>
            <div className={styles.coinSide}>
                {showUpgradeArrow && (
                    <div className={styles.upgradeArrow}>
                        <span className={styles.prevTierDot} style={{ borderColor: prevColor, color: prevColor }}>
                            {prevLabel?.[0]}
                        </span>
                        <span className={styles.arrow} style={{ color: prevColor }}>→</span>
                    </div>
                )}
                <div
                    className={`${styles.coinWrap} ${isLegendary ? styles.coinWrapLegendary : ''} ${isCollection ? styles.coinWrapLocked : ''}`}
                    style={{ borderColor: rc.color, boxShadow: `0 0 18px ${rc.glow}` }}
                >
                    {isCollection && !coin?.coinImageUrl ? (
                        <div className={styles.lockedCoin}><span className={styles.lockIcon}>🔒</span></div>
                    ) : coin?.coinImageUrl ? (
                        // Feed never autoplays video: Signature coins show their poster + marker
                        <img src={resolveCoinMedia(coin).posterUrl} alt="coin" className={styles.coinImg} />
                    ) : (
                        <div className={styles.coinFallback} style={{ color: rc.color }}>
                            <span className={styles.coinFallbackLetter}>{rc.label[0]}</span>
                        </div>
                    )}
                    {isLegendary && <div className={styles.legendaryShimmer} />}
                </div>
                <SignatureBadge coin={coin} compact />
            </div>
            <PosterThumb url={posterUrl} title={mediaTitle} />
        </div>
    );
}

function ReactionBar({ myReaction, counts, onReact }) {
    return (
        <div className={styles.reactions}>
            {REACTIONS.map(r => {
                const isActive = myReaction === r.id;
                const count    = counts[r.id] ?? 0;
                return (
                    <button
                        key={r.id}
                        className={`${styles.reactionPill} ${isActive ? styles.reactionPillActive : ''}`}
                        onClick={() => onReact(r.id)}
                        title={r.label}
                    >
                        <span className={styles.reactionEmoji}>{r.emoji}</span>
                        <span className={styles.reactionLabel}>{r.label}</span>
                        {count > 0 && <span className={styles.reactionCount}>{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

// ─── Feed card ────────────────────────────────────────────────────────────────

function FeedCard({ event, myTier, onTryIt, onUserClick, isLoggedIn }) {
    const serverReactions = event.reactions ?? {};
    const [myReaction, setMyReaction] = useState(serverReactions.myReaction ?? null);
    const [counts, setCounts] = useState({
        impressive:  serverReactions.impressive  ?? 0,
        legendary:   serverReactions.legendary   ?? 0,
        trying:      serverReactions.trying      ?? 0,
        watchlisted: serverReactions.watchlisted ?? 0,
    });

    // toast: null | { variant: 'prompt'|'already'|'success'|'error'|'reactionError', media }
    const [toast, setToast] = useState(null);
    // Prevent rapid double-taps triggering multiple watchlist checks
    const pendingWatchlist = useRef(false);

    const type        = event.type ?? event.eventType ?? '';
    const isLegendary = type === 'legendary_unlocked';
    const isTrending  = type === 'trending';
    const coin        = normaliseCoin(event.coin);
    const rarity      = coin?.rarity ?? 'bronze';
    const rc          = RARITY_CONFIG[rarity] ?? RARITY_CONFIG.bronze;
    const meta        = getActivityMeta(event);
    const cta         = resolveCTA(meta.cta, myTier);

    const dismissToast = useCallback(() => setToast(null), []);

    async function handleWatchlistAdd(media) {
        try {
            await WatchlistService.addToWatchlist(media.tmdbId, media.kind);
            setToast({ variant: 'success', media });
            // TODO: analytics — watchlist_added_from_feed_reaction
        } catch (err) {
            // 409 = already in watchlist (race condition / stale check)
            if (err?.status === 409 || err?.response?.status === 409) {
                setToast({ variant: 'already', media });
            } else {
                setToast({ variant: 'error', media });
            }
        }
    }

    async function react(id) {
        // ── Auth gate ──────────────────────────────────────────────────────────
        if (!isLoggedIn) {
            alert('Create an account to react and save titles to your watchlist.');
            return;
        }

        const isSame      = myReaction === id;
        const newReaction = isSame ? null : id;

        // ── Optimistic update ──────────────────────────────────────────────────
        const prevReaction = myReaction;
        const prevCounts   = { ...counts };

        setCounts(prev => {
            const next = { ...prev };
            if (myReaction)  next[myReaction]  = Math.max(0, (next[myReaction]  ?? 0) - 1);
            if (newReaction) next[newReaction] = (next[newReaction] ?? 0) + 1;
            return next;
        });
        setMyReaction(newReaction);

        // TODO: analytics — feed_reaction_clicked

        // ── Persist ────────────────────────────────────────────────────────────
        try {
            await FeedService.react(event.id, newReaction);
        } catch {
            // Revert on failure
            setCounts(prevCounts);
            setMyReaction(prevReaction);
            setToast({ variant: 'reactionError', media: null });
            return;
        }

        // ── Watchlisted special flow ───────────────────────────────────────────
        if (id === 'watchlisted' && newReaction === 'watchlisted') {
            if (pendingWatchlist.current) return;
            pendingWatchlist.current = true;

            // TODO: analytics — feed_watchlisted_reaction_clicked

            const media = getWatchlistableMedia(event);

            if (!media) {
                // No watchlistable media on this card — reaction saved, no prompt needed
                pendingWatchlist.current = false;
                return;
            }

            try {
                const alreadyOn = await WatchlistService.isOnWatchlist(media.tmdbId, media.kind);

                if (alreadyOn) {
                    setToast({ variant: 'already', media });
                    // TODO: analytics — watchlist_prompt_shown (already_on_watchlist)
                } else {
                    setToast({ variant: 'prompt', media });
                    // TODO: analytics — watchlist_prompt_shown
                }
            } catch {
                // Check failed silently — reaction is still saved, no prompt shown
            } finally {
                pendingWatchlist.current = false;
            }
        } else {
            // Switching to a different reaction or removing the watchlisted reaction:
            // close any open toast but do NOT touch the actual watchlist.
            setToast(null);
        }
    }

    function handleCTA() {
        if (cta.disabled) return;
        const media = coin?.media?.tmdbId
            ? { tmdbId: coin.media.tmdbId, kind: coin.media.kind ?? 'movie' }
            : event.media ?? null;
        if (media) onTryIt?.(media);
    }

    const cardClass = [
        styles.card,
        isLegendary ? styles.legendaryCard : '',
        isTrending  ? styles.trendingCard  : '',
    ].filter(Boolean).join(' ');

    return (
        <article className={cardClass}>
            {isLegendary && (
                <div className={styles.legendaryBanner}>✦ LEGENDARY UNLOCKED ✦</div>
            )}

            {/* Header */}
            <div className={styles.cardHeader}>
                <button className={styles.userBtn} onClick={() => onUserClick?.(event.user?.username)}>
                    <div className={styles.avatar} style={isLegendary ? { borderColor: rc.color } : {}}>
                        {event.user?.avatarUrl
                            ? <img src={event.user.avatarUrl} alt="" />
                            : <span>{event.user?.username?.[0]?.toUpperCase()}</span>
                        }
                    </div>
                    <div className={styles.userMeta}>
                        <span className={styles.username}>
                            {isTrending ? '🔥 Trending' : `@${event.user?.username}`}
                            {!isTrending && <FoundingCollectorIcon isFoundingCollector={event.user?.isFoundingCollector} />}
                        </span>
                        <span className={styles.timeAgo}>{timeAgo(event.timestamp ?? event.createdAt)}</span>
                    </div>
                </button>
                {meta.tierBadge && (
                    <span
                        className={`${styles.tierBadge} ${isLegendary ? styles.tierBadgeLegendary : ''}`}
                        style={{ color: meta.tierBadge.color, borderColor: meta.tierBadge.color }}
                    >
                        {isLegendary ? '✦ ' : '◆ '}{meta.tierBadge.label}
                    </span>
                )}
            </div>

            {/* Narrative */}
            <div className={styles.narrative}>
                <p className={styles.activitySentence}>
                    {meta.sentence}
                    {meta.sentenceSuffix && (
                        <span className={styles.legendarySuffix}> {meta.sentenceSuffix}</span>
                    )}
                </p>
                {meta.title && (
                    <p className={`${styles.achievementTitle} ${isLegendary ? styles.achievementTitleLegendary : ''}`}>
                        {meta.title}
                    </p>
                )}
            </div>

            {/* Coin + Poster */}
            <CoinVisual
                event={event} coin={coin} rc={rc} isLegendary={isLegendary}
                showUpgradeArrow={meta.showUpgradeArrow}
                prevColor={meta.prevColor} prevLabel={meta.prevLabel}
            />

            {/* Context & status */}
            <div className={styles.contextBlock}>
                {meta.context && <p className={styles.contextLine}>{meta.context}</p>}
                {meta.status && (
                    <p className={`${styles.statusLine} ${isLegendary ? styles.statusLineLegendary : ''}`}>
                        {meta.status}
                    </p>
                )}
            </div>

            {/* Reactions */}
            <ReactionBar myReaction={myReaction} counts={counts} onReact={react} />

            {/* Watchlist / reaction-error toast — shown between reactions and CTA */}
            {toast && toast.variant !== 'reactionError' && (
                <WatchlistToast
                    title={toast.media?.title ?? 'this title'}
                    variant={toast.variant}
                    onAdd={() => handleWatchlistAdd(toast.media)}
                    onDismiss={dismissToast}
                />
            )}
            {toast?.variant === 'reactionError' && (
                <div className={`${styles.toast} ${styles.toast_error}`} role="alert">
                    <div className={styles.toastContent}>
                        <span className={styles.toastBody}>Couldn't update reaction. Please try again.</span>
                    </div>
                    <div className={styles.toastActions}>
                        <button className={styles.toastDismissBtn} onClick={dismissToast} aria-label="Dismiss">✕</button>
                    </div>
                </div>
            )}

            {/* CTA */}
            <div className={styles.ctaRow}>
                <button
                    className={[
                        styles.ctaBtn,
                        isLegendary               ? styles.ctaBtnLegendary : '',
                        cta.variant === 'upgrade' ? styles.ctaBtnUpgrade   : '',
                        cta.variant === 'owned'   ? styles.ctaBtnOwned     : '',
                        cta.disabled              ? styles.ctaBtnDisabled  : '',
                    ].filter(Boolean).join(' ')}
                    onClick={handleCTA}
                    disabled={cta.disabled}
                >
                    {cta.label}
                </button>
                {cta.subLabel && <p className={styles.ctaSubLabel}>{cta.subLabel}</p>}
            </div>
        </article>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyFeed() {
    return (
        <div className={styles.empty}>
            <span className={styles.emptyIcon}>🎬</span>
            <h3 className={styles.emptyTitle}>Your feed is quiet</h3>
            <p className={styles.emptySub}>Follow other collectors to see their achievements here</p>
            <Link to="/discover" className={styles.emptyBtn}>Find Collectors →</Link>
        </div>
    );
}

// The single coin card (with reactions), reused by the home feed's Following tab.
export { FeedCard as CoinActivityCard, buildMyTierMap };

// ─── Main export ──────────────────────────────────────────────────────────────

export default function ActivityFeed({ events = [], loading, myCoins = [], onTryIt, onUserClick, isLoggedIn = true }) {
    const myTierMap = buildMyTierMap(myCoins);

    if (loading) {
        return (
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>⚡ Happening Now</h2>
                {[1, 2, 3].map(i => <div key={i} className={styles.skeleton} />)}
            </section>
        );
    }

    // When there are no events, render nothing — the parent (Home) renders the
    // new-user modules in place of this component's old empty state.
    if (events.length === 0) return null;

    return (
        <section className={styles.section}>
            <h2 className={styles.sectionTitle}>⚡ Happening Now</h2>
            {events.map(e => (
                <FeedCard
                    key={e.id}
                    event={e}
                    myTier={myTierMap[String(e.coin?.tmdbId ?? '')] ?? null}
                    onTryIt={onTryIt}
                    onUserClick={onUserClick}
                    isLoggedIn={isLoggedIn}
                />
            ))}
        </section>
    );
}